import type { ImageFile } from "@/utils/types/types";

/**
 * Reusable images that are always selectable in the LineItemForm dropdown,
 * regardless of what the admin has uploaded. Handy for custom items that
 * repeatedly use the same artwork / placeholder image.
 *
 * To add one: upload the image to S3 once, grab its URL, and add an entry
 * below via `reusableImage(...)`. If you have a separate raw/thumbnail
 * variant pass them too; otherwise the main url is used for all three.
 */
function reusableImage(opts: {
  name: string;
  url: string;
  rawUrl?: string;
  thumbnailUrl?: string;
}): ImageFile {
  return {
    name: opts.name,
    url: opts.url,
    rawUrl: opts.rawUrl ?? opts.url,
    thumbnailUrl: opts.thumbnailUrl ?? opts.url,
    pixelHeight: 0,
    pixelWidth: 0,
    realHeight: 0,
    realWidth: 0,
    resolution: 0,
    updatedAt: "",
  };
}

export const REUSABLE_IMAGES: ImageFile[] = [
  // Placeholder for supplied prints with no artwork on file (Create Item).
  // Uploaded to S3/CloudFront so the URL is ABSOLUTE: Stripe rejects relative
  // paths on product_data.images (see tpw-server-functions stripe.ts guard),
  // and an absolute URL also lets the placeholder thumbnail render on the
  // Stripe checkout page + receipt. ⚠ Do not delete this upload from the
  // account's Uploads, or the placeholder 404s.
  // Rebuilt 2026-09-03: 8000x12000 px (2:3 portrait, text in the middle
  // third so any crop keeps it), pinned in the admin account by file name —
  // see utils/constants/placeholderUploads.ts.
  reusableImage({
    name: "CustomerSuppliedArt.jpg",
    url: "https://dk5cww83sd1n4.cloudfront.net/verified-user/dXNlci10ZXN0LTMzZmM2NDQ2LTY5YjktNDc1Mi05MjA5LTgwODAzYzNhNWU0MA/CustomerSuppliedArt_eJo69n6Q_compressed.jpg",
    rawUrl:
      "https://dk5cww83sd1n4.cloudfront.net/verified-user/dXNlci10ZXN0LTMzZmM2NDQ2LTY5YjktNDc1Mi05MjA5LTgwODAzYzNhNWU0MA/CustomerSuppliedArt_eJo69n6Q_raw.jpg",
    thumbnailUrl:
      "https://dk5cww83sd1n4.cloudfront.net/verified-user/dXNlci10ZXN0LTMzZmM2NDQ2LTY5YjktNDc1Mi05MjA5LTgwODAzYzNhNWU0MA/CustomerSuppliedArt_eJo69n6Q_thumbnail.jpg",
  }),
  reusableImage({
    name: "DBL.jpg",
    url: "https://dk5cww83sd1n4.cloudfront.net/reusable/DBL_hl0iqrMc_compressed.jpg",
    rawUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/DBL_hl0iqrMc_raw.jpg",
    thumbnailUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/DBL_hl0iqrMc_thumbnail.jpg",
  }),
  reusableImage({
    name: "DHL.png",
    url: "https://dk5cww83sd1n4.cloudfront.net/reusable/DHL_aWQVb06b_compressed.jpg",
    rawUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/DHL_aWQVb06b_raw.jpg",
    thumbnailUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/DHL_aWQVb06b_thumbnail.jpg",
  }),
  reusableImage({
    name: "UPS.jpg",
    url: "https://dk5cww83sd1n4.cloudfront.net/reusable/UPS_qb4BWIM8_compressed.jpg",
    rawUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/UPS_qb4BWIM8_raw.jpg",
    thumbnailUrl:
      "https://dk5cww83sd1n4.cloudfront.net/reusable/UPS_qb4BWIM8_thumbnail.jpg",
  }),
];
